import { useMemo } from 'react';
import { bossDisplayName } from '../../../shared/domain/bossPresentation.js';
import { getAlternativeTetrisCandidates } from '../domain/raidTetrisCandidateSlots.js';
import { RaidTetrisMiniDeck } from './RaidTetrisMiniDeck.jsx';

function memberOpenid(value = {}) {
  return String(value.openid || value.memberOpenid || value.member_openid || '').trim();
}

function formatEok(value) {
  return `${Number(value || 0).toLocaleString('ko-KR', { maximumFractionDigits: 3 })}억`;
}

function formatAvailability(member = {}) {
  const windows = Array.isArray(member.availabilityWindows) ? member.availabilityWindows : [];
  if (!windows.length) return '미설정';
  return windows.map((item) => `${item.start}~${item.end}`).join(', ');
}

function candidateSlotTitle(candidate = {}) {
  return String(candidate.title || '').trim() || `모의전 ${candidate.slotNo || '-'}`;
}

export function RaidTetrisCandidateSelector({
  selectedBoss = null,
  members = [],
  candidates = [],
  excludedMemberKeys = new Set(),
  excludedSlotIds = new Set(),
  recommendedIds = new Set(),
  actualCountByMember = new Map(),
  preferAvailability = false,
  currentOnly = false,
  onChangeAvailabilityPreference = () => {},
  onToggleMember = () => {},
  onToggleSlot = () => {},
  onSelectAll = () => {},
  onClearAll = () => {},
  onKeepRecommended = () => {},
}) {
  const bossNo = Number(selectedBoss?.bossNo || 0);
  const bossCandidates = useMemo(() => candidates
    .filter((candidate) => Number(candidate.bossNo) === bossNo), [bossNo, candidates]);

  const candidatesByMember = useMemo(() => {
    const map = new Map();
    for (const candidate of bossCandidates) {
      const list = map.get(candidate.memberOpenid) || [];
      list.push(candidate);
      map.set(candidate.memberOpenid, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => {
        const aRecommended = recommendedIds.has(a.id) ? 1 : 0;
        const bRecommended = recommendedIds.has(b.id) ? 1 : 0;
        return bRecommended - aRecommended || b.damageEok - a.damageEok || a.slotNo - b.slotNo;
      });
    }
    return map;
  }, [bossCandidates, recommendedIds]);

  const visibleMembers = members
    .filter((member) => candidatesByMember.has(memberOpenid(member)))
    .sort((a, b) => {
      const aOpenid = memberOpenid(a);
      const bOpenid = memberOpenid(b);
      const aPicked = (candidatesByMember.get(aOpenid) || []).some((candidate) => recommendedIds.has(candidate.id));
      const bPicked = (candidatesByMember.get(bOpenid) || []).some((candidate) => recommendedIds.has(candidate.id));
      if (aPicked !== bPicked) return aPicked ? -1 : 1;
      const aCandidate = candidatesByMember.get(aOpenid)?.[0];
      const bCandidate = candidatesByMember.get(bOpenid)?.[0];
      if (preferAvailability && Boolean(aCandidate?.isAvailableNow) !== Boolean(bCandidate?.isAvailableNow)) return aCandidate?.isAvailableNow ? -1 : 1;
      const aBest = aCandidate?.damageEok || 0;
      const bBest = bCandidate?.damageEok || 0;
      return bBest - aBest || String(a.nickname || '').localeCompare(String(b.nickname || ''), 'ko');
    });

  const memberStateKey = (openid) => `${bossNo}:${openid}`;
  const memberChecked = (openid) => !excludedMemberKeys.has(memberStateKey(openid));
  const slotChecked = (candidate) => !excludedSlotIds.has(candidate.id);
  const selectedMemberCount = visibleMembers.filter((member) => memberChecked(memberOpenid(member))).length;
  const selectedSlotCount = bossCandidates.filter((candidate) => memberChecked(candidate.memberOpenid) && slotChecked(candidate)).length;

  return (
    <section className="raid-tetris-selected-boss-candidates">
      <header className="raid-tetris-candidate-toolbar">
        <div className="raid-tetris-candidate-toolbar-copy">
          <div><strong>{bossDisplayName(selectedBoss, bossNo)} 추천 후보</strong><span>{selectedMemberCount}/{visibleMembers.length}명 · {selectedSlotCount}/{bossCandidates.length}슬롯 포함</span></div>
          <p>추천에 포함할 멤버와 모의전 슬롯을 선택합니다.</p>
          <div className="raid-tetris-availability-options">
            <label className="raid-tetris-availability-preference">
              <input type="checkbox" checked={preferAvailability} onChange={(event) => onChangeAvailabilityPreference('preferAvailability', event.target.checked)} />
              <span><strong>등록 접속시간 우선</strong><small>멤버가 설정한 시간대 기준으로 지금 가능하거나 다음 접속이 가까운 멤버를 우선합니다.</small></span>
            </label>
            <label className="raid-tetris-availability-preference">
              <input type="checkbox" checked={currentOnly} onChange={(event) => onChangeAvailabilityPreference('currentOnly', event.target.checked)} />
              <span><strong>지금이 등록시간인 멤버만</strong><small>현재 시각이 멤버가 설정한 접속 가능 시간대에 포함되는 후보만 남깁니다.</small></span>
            </label>
          </div>
          <p className="raid-tetris-availability-source-note">위 두 설정은 등록된 접속 가능 시간 기준입니다. 현재 사이트 접속 여부는 포함하지 않으며, 전체 시뮬레이션의 ‘현재 사이트 접속자’ 옵션에서 별도로 반영합니다.</p>
        </div>
        <div className="raid-tetris-candidate-actions" aria-label="후보 일괄 설정">
          <button type="button" onClick={onSelectAll}>전체 포함</button><button type="button" className="primary" onClick={onKeepRecommended}>추천만 사용</button><button type="button" className="exclude" onClick={onClearAll}>전체 제외</button>
        </div>
      </header>

      {visibleMembers.length ? <div className="raid-tetris-selected-boss-candidate-grid">{visibleMembers.map((member) => {
        const openid = memberOpenid(member);
        const memberCandidates = candidatesByMember.get(openid) || [];
        const checked = memberChecked(openid);
        const attackCount = actualCountByMember.get(openid) || 0;
        const recommendedCandidate = memberCandidates.find((candidate) => recommendedIds.has(candidate.id));
        const enabledSlots = memberCandidates.filter(slotChecked);
        const representative = recommendedCandidate || enabledSlots[0] || memberCandidates[0];
        const representativeActive = Boolean(representative && slotChecked(representative));
        const alternativeCandidates = getAlternativeTetrisCandidates(memberCandidates, representative);
        const picked = Boolean(recommendedCandidate);
        const status = representative?.isAvailableNow ? '설정시간 내' : (representative?.availabilityLabel || '접속시간 미설정');
        return (
          <article className={`raid-tetris-selected-boss-candidate ${checked ? 'active' : 'off'} ${picked ? 'picked' : ''}`} key={openid}>
            <header className="raid-tetris-candidate-member-head"><div className="raid-tetris-candidate-member-copy"><div><strong>{member.nickname}</strong><em className={picked ? 'recommended' : ''}>{picked ? '현재 추천' : '대체 후보'}</em></div><small>실전 {attackCount}/3 · {status} · {formatAvailability(member)} · 모의전 {memberCandidates.length}개</small></div><button type="button" className={`raid-tetris-member-candidate-toggle ${checked ? 'active' : ''}`} onClick={() => onToggleMember(openid)} aria-pressed={checked}>{checked ? '포함' : '제외'}</button></header>
            <label className={`raid-tetris-candidate-representative ${representativeActive && checked ? 'active' : ''} ${picked ? 'recommended' : ''}`}>
              <input type="checkbox" checked={representativeActive && checked} disabled={!checked || !representative} onChange={() => representative && onToggleSlot(representative.id, representative.memberOpenid)} aria-label={`${member.nickname} ${candidateSlotTitle(representative)} 추천 포함`} />
              <span className="raid-tetris-candidate-slot-copy"><strong>{candidateSlotTitle(representative)}</strong></span>
              <RaidTetrisMiniDeck deck={representative?.deck || []} />
              <b>{formatEok(representative?.damageEok)}</b>
            </label>
            {alternativeCandidates.length ? (
              <details className="raid-tetris-candidate-slot-section"><summary><span>다른 모의전 슬롯</span><small>{alternativeCandidates.length}개</small><i aria-hidden="true">⌄</i></summary><div className="raid-tetris-candidate-slot-list">{alternativeCandidates.map((candidate) => {
                const slotActive = slotChecked(candidate);
                const isRecommended = recommendedIds.has(candidate.id);
                return <label className={`${slotActive ? 'active' : ''} ${isRecommended ? 'recommended' : ''}`} key={candidate.id}><input type="checkbox" checked={slotActive && checked} disabled={!checked} onChange={() => onToggleSlot(candidate.id, candidate.memberOpenid)} /><span className="raid-tetris-candidate-slot-copy"><strong>{candidateSlotTitle(candidate)}</strong></span><RaidTetrisMiniDeck deck={candidate.deck || []} compact /><b>{formatEok(candidate.damageEok)}</b></label>;
              })}</div></details>
            ) : null}
          </article>
        );
      })}</div> : <p className="raid-tetris-recommend-empty">{currentOnly ? '현재 시각이 등록된 접속 시간대에 해당하는 후보가 없습니다.' : '이 보스에 등록된 모의전 기록이 없습니다.'}</p>}
    </section>
  );
}
